import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import mapboxgl from "mapbox-gl";
import { Buffer } from 'buffer';
import { emptyGeoJSON } from "core/ui/mapping/map";
import Field from "core/ui/forms/field";
import recolor_svg from "core/utils/recolor_svg";
import getSvgImgSrc from "../../core/utils/getSvgImgSrc";
import { pulseLayer } from "../../core/ui/mapping/map";
import useDraggableRadius from "hooks/useDraggableRadius";
import SessionContext from "SessionContext";

export default function LocationsLayers(props) {
  const session = useContext(SessionContext);
  const [mapLayersReady, setMapLayersReady] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [locationTypes, setLocationTypes] = useState([]);
  const [locationTypesLoaded, setLocationTypesLoaded] = useState(false);
  const [hasFilters, setHasFilters] = useState(session.crossfilterStore.filters.locations ? true : false);
  const [layerState, setLayerState] = useState({});
  const [savedSelectedLocationTypeIds, setSavedSelectedLocationTypeIds] =
    useState(session.crossfilterStore.location_type_ids || []);
  const { redraw_location_radius_filters } = useDraggableRadius(props.map, onSetRadius);
  const [selectedLocationNames, setSelectedLocationNames] = useState([]);
  const [allLocationOptions, setAllLocationOptions] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [defaultRadius, setDefaultRadius] = useState("3");

  let _cancelPulseLayer = undefined;

  // load default radius value for the company
  function loadSystemRadius() {
    session
      .query("location/company-radius-defaults", {am_company_id: session.appState.activeCompany.id})
      .then((response) => {
        setDefaultRadius(response.default_radius_selection.toString());
      })
      .catch((error) => {
        console.log("Failed to load default radius: ", JSON.stringify(error));
      });
  }

  useEffect(() => { 
    const locs = session.crossfilterStore.filters;
    setHasFilters((locs && locs.length !== 0) ? true : false);    
  }, [session.crossfilterStore.filters]);

  useEffect(() => { 
    if (!hasFilters) {
      setLayerState({});
    }
    redrawLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFilters]);

  useLayoutEffect(() => {
    loadSystemRadius();
    getAllLocationOptions();
    return () => {
      if (!props.map._removed && props.map.getLayer("locations"))
        props.map.removeLayer("locations");
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!props.show) {
      return;
    }
    initializeMapLayers();
    loadLocations();

    return () => {
      _cancelPulseLayer && _cancelPulseLayer();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-initialize map markers when locationTypesData becomes available
  useEffect(() => {
    if (!props.show || !mapLayersReady || locationTypesLoaded) return;
    const location_types = session.crossfilterStore.locationTypesData;
    if (Array.isArray(location_types)) {
      const map = props.map;
      for (const location_type of location_types) {
        const imageName = `map-marker-${location_type.name}`;
        const buff = Buffer.from(
          recolor_svg(location_type.svg, location_type.color, "currentColor")
        );
        const base64data = buff.toString("base64");
        const src = `data:image/svg+xml;base64,${base64data}`;
        map.addNamedImage(imageName, src, 25, 25);
      }
      setLocationTypes(location_types);
      setLocationTypesLoaded(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.crossfilterStore.locationTypesData, mapLayersReady, session.crossfilterStore.explorerModule]);

  useEffect(() => {
    redrawLocations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, layerState, session.crossfilterStore.location_type_points]);

  useEffect(() => {
    redraw_location_radius_filters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.crossfilterStore.filters]);

  useEffect(() => {
    if (mapLayersReady && locationTypesLoaded && locationsLoaded && hasFilters) {
      const map = props.map;
      //set map layers for loaded audience
      savedSelectedLocationTypeIds.length && savedSelectedLocationTypeIds.forEach((id) => {
        const locationType = locationTypes?.find((l) => l?.id === id) || null;
        if (locationType && id) {
          setLayerState((prevVal) => {
            let name = locationType.name;
            const newState = { ...prevVal, [name]: true };
            updateLocationTypePoints(name, true);
            return newState;
          });
        }
      });
      setSavedSelectedLocationTypeIds([]); //clear after loading
      let initiateRedraw = false;
      for (let location_type of locationTypes) {
        const setting = layerState[location_type.name] ? "visible" : "none";
        if (map.getLayer(`locations-${location_type.name}`)) {
          map.setLayoutProperty(
            `locations-${location_type.name}`,
            "visibility",
            setting
          );
        }
        initiateRedraw = true;
      }
      initiateRedraw && redrawLocations();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mapLayersReady,
    layerState,
    locationTypes,
    locationTypesLoaded,
    locationsLoaded,
  ]);

  function initializeMapLayers() {
    const map = props.map;
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      anchor: "top",
    });

    if (!map.getSource("locations")) {
      map.addSource("locations", emptyGeoJSON());
    }
    if (!map.getLayer("locations")) {
      map.addLayer({
        id: "locations",
        source: "locations",
        type: "symbol",
        layout: {
          "icon-image": "map-marker-{location_type__name}",
          "icon-anchor": "bottom",
          "icon-allow-overlap": true,
        },
        paint: {
          "icon-opacity": 1,
        },
      });
    }
    bindLocationPopupToLayer(map, "locations", popup);

    // Use cached locationTypesData from crossfilterStore instead of fetching
    const location_types = session.crossfilterStore.locationTypesData;
    if (Array.isArray(location_types)) {
      for (const location_type of location_types) {
        const imageName = `map-marker-${location_type.name}`;
        const buff = Buffer.from(
          recolor_svg(location_type.svg, location_type.color, "currentColor")
        );
        const base64data = buff.toString("base64");
        const src = `data:image/svg+xml;base64,${base64data}`;
        map.addNamedImage(imageName, src, 25, 25);
      }
      setLocationTypes(location_types);
      setLocationTypesLoaded(true);
    }
    if (!map.getLayer("locations-establishing")) {
      map.addLayer({
        id: "locations-establishing",
        source: "locations",
        type: "symbol",
        filter: ["boolean", ["get", "establishing"], false],
        layout: {
          "icon-image": "map-marker-{location_name}",
          "icon-anchor": "bottom",
          "text-field": "Establishing Search Radius",
          "text-size": 11,
          "text-anchor": "top",
        },
        paint: {
          "text-color": "#333",
          "text-halo-color": "rgba(242,243,240,.3)",
          "text-halo-width": 2,
          "text-halo-blur": 1,
        },
      });
    }
    setMapLayersReady(true);
  }

  async function onSetRadius(location, filter_entry) {
    const loc = locations.find((l) => l.id === location.id);
    if (loc) {
      loc.establishing = true;
      const { series } = {
        series: `DIST_FROM_${location.lng}_${location.lat}`,
      }; // await ensure_location_radius_filter(location.lng, location.lat);
      filter_entry.series = series;
      loc.establishing = false;
      redrawLocations();
    }
  }

  function addLocationRadii(locations) {
    let filters = [];
    locations.forEach((location) => {
      const new_filter = {
        series: location.series,
        value: location.default_radius,
        isNegated: false,
        location,
      };
      // remove svg text since it isn't needed here and takes up a lot of space
      delete new_filter.location.location_type__svg;
      filters.push(new_filter);
      onSetRadius(location, new_filter);
    });
  }

  function bindLocationPopupToLayer(map, layer, popup) {
    map.on("mouseenter", layer, function (e) {
      let coordinates = e.features[0].geometry.coordinates.slice();

      // Populate the popup and set its coordinates
      // based on the feature found.
      popup
        .setLngLat(coordinates)
        .setHTML(e.features[0].properties.name)
        .addTo(map);
    });

    map.on("mouseleave", layer, function () {
      popup.remove();
    });
  }

  // Use cached mapList from crossfilterStore instead of fetching per-component
  function loadLocations() {
    if (session.crossfilterStore.mapListData) {
      setLocations(session.crossfilterStore.mapListData);
      setLocationsLoaded(true);
    }
  }

  function redrawLocations() {
    locationTypesLoaded &&
      locationsLoaded && 
      props.map.getSource("locations").setData({
        type: "FeatureCollection",
        features: ((hasFilters) ? session.crossfilterStore.location_type_points || []: []).map(
          (point) => ({
            type: "Feature",
            properties: point,
            geometry: {
              type: "Point",
              coordinates: [point.lng, point.lat],
            },
          })
        ),
      });
    if (locations.some((loc) => loc.establishing)) {
      if (!_cancelPulseLayer) {
        _cancelPulseLayer = pulseLayer(
          props.map,
          "locations-establishing",
          2000
        );
      }
    } else {
      _cancelPulseLayer && _cancelPulseLayer();
      _cancelPulseLayer = undefined;
    }
  }

  useEffect(() => {
    setSelectedLocationNames(session.crossfilterStore.filters);
    loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.crossfilterStore.filters, session.crossfilterStore.mapListData])

  function getAllLocationOptions() {
    // get all location with location_type
    let values = Object.keys(session.chartsStore.charts).filter(function(el) {
      return /^location_type.*?/i.test(el);
    });

    // get the full objects
    let fullValues = [];
    values.forEach(function(key) {
      fullValues.push(session.chartsStore.charts[key]); 
    });

    setAllLocationOptions(fullValues);
  }

  function removeDuplicates(arr) {
    return arr.filter((item,
      index) => arr.indexOf(item) === index);
  }

  function updateLocationTypePoints(name, add, selectionFullObject) {
    const selected_locations = [];
    locations.forEach(
      (l) => {
        if(l.location_type__name === name) {
          let trimmed = {...l, name: l.name.trim()};
          selected_locations.push(trimmed);
        }
      }
    );
    // to select all values when adding
    if(name && selectionFullObject) {
      let is_negated = 0;
      let company_code = session.appState.activeCompany.code;
      let population_domain = session.crossfilterStore.explorerModule;
      let series = selectionFullObject.id && `${company_code}_location_types_${population_domain}_app.${selectionFullObject.id}`;
      let values = [];
      selected_locations.length && selected_locations.forEach((val) => {
        values.push(val.name.trim())
      })
      let removeDups = removeDuplicates(values);
      values = removeDups;

      if (add && selected_locations && selectionFullObject) {
        selectionFullObject.id && session.crossfilterStore.add_location_type_filter(selectionFullObject.id);
        session.crossfilterStore.add_location_type_icon(selected_locations);
        addLocationRadii(selected_locations);
        session.crossfilterStore.add_filter(selectionFullObject.id, series, values, is_negated);
      } else if(selected_locations && selectionFullObject) {
        selectionFullObject.id && session.crossfilterStore.remove_location_type_filter(selectionFullObject.id);
        session.crossfilterStore.remove_location_type_icon(selected_locations);
        localStorage.removeItem("explorer:layers:locations");
      }
    } else {
      // load the icon info when reloading a saved audience
      if(session.crossfilterStore.audience.id) {
        console.log("loading saved audience")
        session.crossfilterStore.add_location_type_icon(selected_locations);
      }
    }
    session.crossfilterStore.setLoadingData(false);
    
  }

  function onChangeCheckbox(val, ev) {
    if (ev && ev.stopPropagation) ev.stopPropagation();

    // force the progress loader(s) on so that users can't keep clicking.
    session.crossfilterStore.setLoadingData(true);
  
    // find the one that matches the label value
    let selectionFullObject = findTheLabelMatch(val);
    
    setLayerState((prevVal) => {
      const newState = { ...prevVal, [val.name]: !!ev.target.checked };
      //Find locations for this location type
      let add = ev.target.checked;
      updateLocationTypePoints(val.name, add, selectionFullObject);
      return newState;
    });
  }

  function findTheLabelMatch(val) {
    // get the full objects
    let fullValues = allLocationOptions;

    // find the one that matches the label value
    let selectionFullObject = {};
    fullValues.forEach((option) => {
      let re = new RegExp(`^${val.name} :`);
      if(option.label.match(re)) {
        let findRadius = option.id.split('_');
        if(findRadius[findRadius.length - 1] === defaultRadius) {
          selectionFullObject = option;
        }
      }
    })
    return selectionFullObject;
  }

  useEffect(() => {
    isLocationChecked();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationNames])


  function isLocationChecked() {
    const keys = Object.keys(selectedLocationNames);
    if(keys[0] === "undefined") {
      keys.shift();
    }
    let selectedList = []
    keys && keys.forEach((option) => {
      let findId = option.split('_');
      let optionId = findId[findId.length - 2];
      optionId && selectedList.push(optionId.toString());
    })
    setSelectedIds(selectedList);
  }

  function checkSelected(id) {
    return selectedIds.includes(id.toString());
  }
 
  return (
    <>
      {" "}
      {locationTypes.length > 0 && (
        <>
          <h4>Location Types</h4>
          <ul>
            {locationTypes.map((location_type) => (
              <li key={location_type.name}>
                <Field
                  label={
                    <>
                      <img
                        style={{ height: 16, width: 16 }}
                        src={getSvgImgSrc(
                          recolor_svg(
                            location_type.svg,
                            location_type.color,
                            "currentColor"
                          )
                        )}
                        alt=""
                      />{" "}
                      {location_type.name}
                    </>
                  }
                >
                  <input
                    type="checkbox"
                    checked={checkSelected(location_type.id)}
                    onChange={onChangeCheckbox.bind(this, location_type)}
                  />
                </Field>
              </li>
            ))}
          </ul>{" "}
        </>
      )}
    </>
  );
}
